/**
 * Shared DOM helpers used by both overlay facades (overlays.js and svgOverlays.js).
 *
 * Contains only utilities that are genuinely cross-overlay.
 * SVG lot-specific DOM mutations (status classes, hover, select, path ID assignment)
 * live in features/searchResultsMap/components/lotPolygons.js.
 *
 * @module mapLibrary/overlayDom
 */

const SVG_NAMESPACE = 'http://www.w3.org/2000/svg';

/**
 * Applies absolute-position styles to a DOM or SVG element.
 * Used to keep the overlay container and the SVG hit layer in sync after each draw().
 *
 */
export function applyPositionStyle(element, left, top, width, height, transform) {
  element.style.left      = `${left}px`;
  element.style.top       = `${top}px`;
  element.style.width     = `${width}px`;
  element.style.height    = `${height}px`;
  element.style.transform = transform;
}

/**
 * Parses an SVG mask string into a <clipPath> element adopted into the current document.
 * Normalises the clipPath id to `clipPathId` to prevent collisions between overlays.
 *
 */
export function parseSvgMaskToClipPath(svgMaskString, clipPathId) {
  const normalizedMask = svgMaskString.replace(/id="[^"]*"/, `id="${clipPathId}"`);
  const parser = new DOMParser();
  const parsed = parser.parseFromString(
    `<svg xmlns="${SVG_NAMESPACE}">${normalizedMask}</svg>`,
    'image/svg+xml'
  );
  const clipPathElement = parsed.querySelector('clipPath');
  if (!clipPathElement) {
    return null;
  }
  return document.adoptNode(clipPathElement);
}

/**
 * Builds a transparent SVG hit layer and its child hit rect, optionally clipped to an SVG mask.
 * Returns both elements so the caller can wire event listeners onto the rect and mount the SVG.
 *
 */
export function buildHitSvgLayer(viewBoxWidth, viewBoxHeight, clipPathId, svgMask, maskClassName) {
  const svgElement = document.createElementNS(SVG_NAMESPACE, 'svg');
  svgElement.setAttribute('viewBox', `0 0 ${viewBoxWidth} ${viewBoxHeight}`);
  svgElement.setAttribute('preserveAspectRatio', 'none');
  if (maskClassName) {
    svgElement.classList.add(maskClassName);
  }

  if (svgMask) {
    const clipPathElement = parseSvgMaskToClipPath(svgMask, clipPathId);
    if (clipPathElement) {
      const defsElement = document.createElementNS(SVG_NAMESPACE, 'defs');
      defsElement.appendChild(clipPathElement);
      svgElement.appendChild(defsElement);
    }
  }

  const hitRect = document.createElementNS(SVG_NAMESPACE, 'rect');
  hitRect.setAttribute('width', String(viewBoxWidth));
  hitRect.setAttribute('height', String(viewBoxHeight));
  hitRect.setAttribute('fill', 'transparent');
  hitRect.setAttribute('pointer-events', 'fill');
  if (svgMask) {
    hitRect.setAttribute('clip-path', `url(#${clipPathId})`);
  }

  svgElement.appendChild(hitRect);

  return { svgElement, hitRect };
}

/**
 * Builds the main SVG element with a <defs> block containing an optional clip-path.
 * The <defs> is always appended so layer groups can later add definitions without restructuring.
 *
 */
export function buildMainSvgElement(width, height, clipPathId, svgMask, maskClassName) {
  const svgElement = document.createElementNS(SVG_NAMESPACE, 'svg');
  svgElement.setAttribute('viewBox', `0 0 ${width} ${height}`);
  svgElement.setAttribute('preserveAspectRatio', 'none');
  if (maskClassName) {
    svgElement.classList.add(maskClassName);
  }

  const defs = document.createElementNS(SVG_NAMESPACE, 'defs');
  if (svgMask) {
    const clipPath = parseSvgMaskToClipPath(svgMask, clipPathId);
    if (clipPath) {
      defs.appendChild(clipPath);
    }
  }
  svgElement.appendChild(defs);

  return svgElement;
}

/**
 * Builds the <g> element that wraps all SVG layers, optionally clipped to the site plan mask.
 *
 */
export function buildLayersGroup(svgMask, clipPathId) {
  const group = document.createElementNS(SVG_NAMESPACE, 'g');
  if (svgMask) {
    group.setAttribute('clip-path', `url(#${clipPathId})`);
  }
  return group;
}

/**
 * Creates one hidden <g> per layer inside parentGroup and returns the layer entry map.
 * Caller is responsible for pre-sorting layers by display order.
 *
 */
export function buildLayerGroups(sortedLayers, parentGroup) {
  const layerEntries = new Map();
  sortedLayers.forEach((layer) => {
    const group = document.createElementNS(SVG_NAMESPACE, 'g');
    group.setAttribute('data-layer-type', layer.svgLayerType);
    group.style.display = 'none';
    parentGroup.appendChild(group);
    layerEntries.set(layer.svgLayerType, {
      groupElement:  group,
      url:           layer.url,
      contentLoaded: false,
      loadingPromise: null,  // in-flight fetch guard (see svgOverlays.js)
      visible:       false,  // explicit visibility state — not derived from style.display
    });
  });
  return layerEntries;
}

/**
 * Parses an SVG text string and appends its root element's children into groupElement.
 * Returns true when content was successfully injected, false when the text is not
 * a valid SVG document.
 *
 * Pre-processing applied before injection:
 * - Removes the `clip-path="url(#mask)"` attribute from `<g id="siteplan_graphic">`.
 *   The outer overlay SVG already clips all content to the community boundary via its
 *   own clip-path, making the internal reference redundant.
 * - Removes the corresponding `<clipPath id="mask">` from `<defs>` so that the element
 *   id is not polluted into the page's global ID space — which would cause the wrong
 *   clip region to be applied when multiple site plans are on screen simultaneously.
 *
 */
export function injectSvgLayerContent(svgText, groupElement) {
  const parser = new DOMParser();
  const parsed = parser.parseFromString(svgText, 'image/svg+xml');
  const root   = parsed.documentElement;
  if (!root || root.tagName.toLowerCase() !== 'svg') {
    return false;
  }

  // Strip internal clip-path that conflicts with the outer SVG's own clip-path.
  root.querySelector('#siteplan_graphic')?.removeAttribute('clip-path');
  root.querySelector('clipPath[id="mask"]')?.remove();

  for (let i = 0; i < root.children.length; i += 1) {
    groupElement.appendChild(document.importNode(root.children[i], true));
  }
  return true;
}

/**
 * Builds a visible SVG element that renders the site plan image clipped to its SvgMask boundary.
 * The SVG <image> element avoids a base64 fetch round-trip and lets the browser
 * load the tile directly while the clip-path masks it to the community shape.
 *
 * When SvgMask is absent: the full rectangular tile is visible.
 *
 */
export function buildImageBoundsSvg(viewBoxWidth, viewBoxHeight, sitePlanId, svgMask, mapImageUrl, maskClassName) {
  const clipPathId = `sp-image-mask-${sitePlanId}`;

  const svgElement = buildMainSvgElement(viewBoxWidth, viewBoxHeight, clipPathId, svgMask, maskClassName);
  svgElement.style.width  = '100%';
  svgElement.style.height = '100%';

  const groupElement = document.createElementNS(SVG_NAMESPACE, 'g');
  if (svgMask) {
    groupElement.setAttribute('clip-path', `url(#${clipPathId})`);
  }

  const imageEl = document.createElementNS(SVG_NAMESPACE, 'image');
  imageEl.setAttribute('href', mapImageUrl);
  imageEl.setAttribute('width', String(viewBoxWidth));
  imageEl.setAttribute('height', String(viewBoxHeight));
  imageEl.setAttribute('preserveAspectRatio', 'none');

  groupElement.appendChild(imageEl);
  svgElement.appendChild(groupElement);

  return { svgElement, imageEl };
}

export function applyLayerClass(groupElement, layerType, layerPrefix) {
  groupElement.classList.add(`${layerPrefix}${layerType.toLowerCase()}`);
}

/**
 * Returns the existing community wrapper div matching the given data-attribute and communityId,
 * or creates and appends a new one to parentElement.
 *
 * Used by both overlays.js (static) and svgOverlays.js (interactive) so the two wrapper
 * strategies stay consistent without duplicating the get-or-create logic.
 *
 * @param {Element} parentElement
 * @param {string}  dataAttributeName  - e.g. 'staticSitePlanCommunityId'
 * @param {string}  communityId
 * @returns {HTMLElement}
 */
export function getOrCreateCommunityWrapper(parentElement, dataAttributeName, communityId) {
  const attrName = dataAttributeName.replace(/([A-Z])/g, '-$1').toLowerCase(); // camelCase → kebab-case
  const selector = `[data-${attrName}="${communityId}"]`;
  const existing = parentElement.querySelector(selector);
  if (existing) {
    return existing;
  }
  const wrapper = document.createElement('div');
  wrapper.dataset[dataAttributeName] = communityId;
  parentElement.appendChild(wrapper);
  return wrapper;
}